import BreadCrumbs, {
    AffinityCrumb,
    CategoryPopoverMenu,
    CategorySelectMenu,
} from 'components/BreadCrumbs';
import {
    useAffinityGroupCategoryChildren,
    useCurrentCountry,
} from 'utils/hooks';
import IndustriesOnlyBreadCrumbs from './IndustriesOnlyBreadcrumbs';
import { categoryQueryFn } from './utils';
import { useEntity } from 'utils/hooks/api';
import { useQuery } from 'react-query';

/**
 * Builds one data source's breadcrumb trail.
 *
 * The affinity crumb calls `useSubcategories` too, not just the two menus;
 * Google's hook is unlike the other three, hence the parameter.
 *
 * `industriesOnlyMetrics` defaults to `metrics`; TikTok is the only source that
 * overrides it, and a call site's own `metrics` prop still wins over both.
 */
export function createSourceBreadCrumbs({
    dataSource,
    useSubcategories,
    useIndustries,
    metrics,
    industriesOnlyMetrics = metrics,
    showIndustryMetrics,
}) {
    const withParentCategory = (Menu) => {
        const SourceMenu = ({ parentId, ...props }) => {
            const parent = useEntity({ id: parentId, dataSource }).data;
            const country = useCurrentCountry();
            const subcategoriesQuery = useSubcategories({ parent, country });
            return (
                <Menu
                    parent={parent}
                    country={country}
                    subcategoriesQuery={subcategoriesQuery}
                    {...props}
                />
            );
        };
        SourceMenu.displayName = `${dataSource}(${Menu.name})`;
        return SourceMenu;
    };

    const SourceAffinityCrumb = ({ id, ...props }) => {
        const country = useCurrentCountry();
        const cats = useAffinityGroupCategoryChildren({
            enabled: !!(id && country?.id),
            categories: [id],
            countries: country?.id ? [country.id] : null,
        });
        const categoryQuery = useQuery({
            queryKey: ['entity', { id, dataSource, minimal: true }],
            queryFn: categoryQueryFn,
        });
        const parent =
            cats.data && categoryQuery.data ? { ...cats.data } : null;
        const subcategoriesQuery = useSubcategories({ parent, country });
        return (
            <AffinityCrumb
                subcategoriesQuery={subcategoriesQuery}
                categoryQuery={categoryQuery}
                country={country}
                cats={cats}
                parent={parent}
                id={id}
                {...props}
            />
        );
    };
    SourceAffinityCrumb.displayName = `${dataSource}(AffinityCrumb)`;

    const CategorySelectMenuComponent = withParentCategory(CategorySelectMenu);
    const CategoryPopoverMenuComponent =
        withParentCategory(CategoryPopoverMenu);

    const SourceBreadCrumbs = (props) => {
        const industriesQuery = useIndustries();
        return (
            <BreadCrumbs
                CategorySelectMenuComponent={CategorySelectMenuComponent}
                CategoryPopoverMenuComponent={CategoryPopoverMenuComponent}
                AffinityCrumbComponent={SourceAffinityCrumb}
                dataSource={dataSource}
                industriesQuery={industriesQuery}
                metrics={metrics}
                showIndustryMetrics={showIndustryMetrics}
                {...props}
            />
        );
    };
    SourceBreadCrumbs.displayName = `${dataSource}BreadCrumbs`;

    const SourceIndustriesOnlyBreadCrumbs = (props) => {
        const industriesQuery = useIndustries();
        return (
            <IndustriesOnlyBreadCrumbs
                metrics={industriesOnlyMetrics}
                showIndustryMetrics={showIndustryMetrics}
                industriesQuery={industriesQuery}
                {...props}
            />
        );
    };
    SourceIndustriesOnlyBreadCrumbs.displayName = `${dataSource}IndustriesOnlyBreadCrumbs`;

    return {
        BreadCrumbs: SourceBreadCrumbs,
        IndustriesOnlyBreadCrumbs: SourceIndustriesOnlyBreadCrumbs,
    };
}
